/**
 * 공지사항 등록/수정 — 레거시 `announcementsForm.js` 핵심 포팅
 */
import type { AnnouncementUpsertRequest } from '@/entities/board/api/board.api';
import {
  useAnnouncement,
  useCreateAnnouncement,
  useUpdateAnnouncement,
  useUploadAnnouncementImage,
} from '@/entities/board/hooks/useBoard';
import { useMenuAccess } from '@/layouts/MenuAccessContext';
import {
  AnnouncementScheduleSection,
  createDefaultScheduleFormState,
  type ScheduleFormState,
} from '@/pages/board/AnnouncementScheduleSection';
import {
  buildAnnouncementSchedulePayload,
  composeDatetimeLocal,
  formatYearMonth,
  getLocalStartOfTodayDatetime,
  getScheduleNoticePublishStartIso,
  isAnnouncementContentEmpty,
  mapScheduleItemsFromAnnouncement,
  removeAnnouncementTableHeightStyles,
  splitDatetimeLocal,
  toAnnouncementPublishIso,
  toScheduleNoticePublishStartLocal,
  validateScheduleRegistrationItems,
} from '@/pages/board/announcementSchedulePharma';
import {
  computeAnnouncementPublicationStatus,
  getAnnouncementPublicationStatusBadgeVariant,
  getAnnouncementPublicationStatusLabel,
} from '@/pages/board/announcementStatusUtils';
import {
  getNoticeAlimtalkSendBadgeVariant,
  getNoticeAlimtalkSendLabel,
  resolveNoticeAlimtalkSendPayload,
  willSendNoticeAlimtalk,
} from '@/pages/board/announcementAlimtalkUtils';
import {
  ANNOUNCEMENT_CATEGORIES,
  ANNOUNCEMENT_FORM_CATEGORIES,
  SCHEDULE_NOTICE_CATEGORY,
} from '@/pages/board/boardUtils';
import { DatePicker } from '@/shared/components/DatePicker';
import { FileUploadButton } from '@/shared/components/FileUploadButton';
import { ImageCropDialog } from '@/shared/components/ImageCropDialog';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import { TinyMceEditor } from '@/shared/components/TinyMceEditor';
import { TimePicker } from '@/shared/components/TimePicker';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Field } from '@/shared/components/ui/form-field';
import { Input } from '@/shared/components/ui/input';
import { FooterRow } from '@/shared/components/ui/row';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Separator } from '@/shared/components/ui/separator';
import { Switch } from '@/shared/components/ui/switch';
import { ROUTE_PATHS, announcementEditPath } from '@/shared/constants/routes';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { ArrowLeft, Megaphone, Pencil, Save } from 'lucide-react';
import React, { useEffect, useState } from 'react';

const defaultPublishRange = () => {
  const start = new Date();
  const end = new Date();
  end.setFullYear(end.getFullYear() + 1);
  const fmt = (d: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  return { start: fmt(start), end: fmt(end) };
};

/** API/표시 시각 → datetime-local 값 */
const toDatetimeLocalValue = (value?: string | null, fallback = ''): string => {
  if (!value) return fallback;
  const normalized = value.includes('T') ? value : value.replace(' ', 'T');
  return normalized.slice(0, 16);
};

const applyScheduleNoticeDefaults = (
  prev: {
    publishStartAt: string;
    publishEndAt: string;
    isPublished: boolean;
  },
  scheduleForm: ScheduleFormState,
): {
  publishStartAt: string;
  publishEndAt: string;
  isPublished: boolean;
  isMainPublished: boolean;
  scheduleForm: ScheduleFormState;
} => {
  const endDate = prev.publishEndAt || defaultPublishRange().end;
  return {
    // 레거시: dayjs().startOf('day') — 로컬 자정
    publishStartAt: getLocalStartOfTodayDatetime(),
    publishEndAt: endDate,
    isPublished: true,
    isMainPublished: true,
    scheduleForm: {
      ...scheduleForm,
      registrationYearMonth:
        scheduleForm.registrationYearMonth ||
        createDefaultScheduleFormState().registrationYearMonth,
      registrationScheduleItems:
        scheduleForm.registrationScheduleItems.length > 0
          ? scheduleForm.registrationScheduleItems
          : createDefaultScheduleFormState().registrationScheduleItems,
    },
  };
};

export const AnnouncementFormPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { id: idParam } = useParams({ strict: false }) as { id?: string };
  const search = useSearch({ strict: false }) as { mode?: string };
  const isNew = !idParam || idParam === 'new';
  const announcementId = isNew ? 0 : Number(idParam);
  const isViewMode = !isNew && search.mode === 'view';
  const { readOnly: menuReadOnly } = useMenuAccess();
  const isReadOnly = isViewMode || menuReadOnly;

  const openEdit = () => {
    navigate({ to: announcementEditPath(announcementId) } as never);
  };

  const { data, isLoading } = useAnnouncement(announcementId);
  const createAnnouncement = useCreateAnnouncement();
  const updateAnnouncement = useUpdateAnnouncement();
  const uploadImage = useUploadAnnouncementImage();

  const [thumbnailPreview, setThumbnailPreview] = useState('');
  const [cropOpen, setCropOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [cropFileName, setCropFileName] = useState('thumbnail.jpg');
  const [scheduleForm, setScheduleForm] = useState<ScheduleFormState>(
    createDefaultScheduleFormState(),
  );
  const [isMainPublished, setIsMainPublished] = useState(false);
  /** 상세 응답의 isAlimtalkSend — 보기 모드 태그용 */
  const [lastAlimtalkSendRequested, setLastAlimtalkSendRequested] = useState<
    boolean | null
  >(null);
  const [alimtalkConfirmOpen, setAlimtalkConfirmOpen] = useState(false);
  const [pendingPayload, setPendingPayload] =
    useState<AnnouncementUpsertRequest | null>(null);

  const range = defaultPublishRange();
  const [form, setForm] = useState({
    title: '',
    category: 'SERVICE_UPDATE',
    content: '',
    isPublished: false,
    publishStartAt: range.start,
    publishEndAt: range.end,
    thumbnailUrl: '',
  });

  const isScheduleNotice = form.category === SCHEDULE_NOTICE_CATEGORY;
  // 일정 안내일 때 알림톡 버튼 노출. 미게시면 비활성 (서버도 발송하지 않음)
  const showAlimtalkSendButton = isScheduleNotice && !isReadOnly;
  const canSendAlimtalk = form.isPublished;

  useEffect(() => {
    if (!data) return;
    const isSchedule = data.category === SCHEDULE_NOTICE_CATEGORY;
    const loadedStart = toDatetimeLocalValue(
      data.publishStartAt ?? data.publishedStartAt,
      range.start,
    );

    setForm({
      title: data.title,
      category: data.category,
      content: data.content ?? '',
      isPublished: data.isPublished,
      // 일정 안내: 레거시 startOf('day') — 시작 시각 00:00
      publishStartAt: isSchedule
        ? toScheduleNoticePublishStartLocal(loadedStart)
        : loadedStart,
      publishEndAt: toDatetimeLocalValue(
        data.publishEndAt ?? data.publishedEndAt,
        range.end,
      ),
      thumbnailUrl: data.thumbnailUrl ?? '',
    });
    setIsMainPublished(data.isMainPublished ?? false);
    setThumbnailPreview(data.thumbnailUrl ?? '');
    // 상세 isAlimtalkSend 는 지난 발송 안내만 — 저장 버튼으로만 발송 여부를 결정
    setLastAlimtalkSendRequested(
      data.isAlimtalkSend == null ? null : Boolean(data.isAlimtalkSend),
    );

    if (isSchedule) {
      setScheduleForm({
        registrationYearMonth:
          formatYearMonth(
            data.schedule?.yearMonth ?? data.registrationYearMonth,
          ) ?? createDefaultScheduleFormState().registrationYearMonth,
        scheduleTopContent:
          data.schedule?.topContent ?? data.scheduleTopContent ?? '',
        scheduleBottomContent:
          data.schedule?.bottomContent ?? data.scheduleBottomContent ?? '',
        registrationScheduleItems: mapScheduleItemsFromAnnouncement(
          data as Parameters<typeof mapScheduleItemsFromAnnouncement>[0],
        ),
      });
    }
  }, [data]);

  // 일정 안내 편집 중에는 게시 시작을 항상 오늘 00:00으로 유지 (저장값과 동일)
  useEffect(() => {
    if (!isScheduleNotice || isReadOnly) return;
    const expected = getLocalStartOfTodayDatetime();
    setForm((prev) =>
      prev.publishStartAt === expected
        ? prev
        : { ...prev, publishStartAt: expected },
    );
  }, [isScheduleNotice, isReadOnly]);
  const handleCategoryChange = (category: string) => {
    if (category === SCHEDULE_NOTICE_CATEGORY) {
      const defaults = applyScheduleNoticeDefaults(form, scheduleForm);
      setForm((prev) => ({
        ...prev,
        category,
        publishStartAt: defaults.publishStartAt,
        publishEndAt: defaults.publishEndAt,
        isPublished: defaults.isPublished,
      }));
      setIsMainPublished(defaults.isMainPublished);
      setScheduleForm(defaults.scheduleForm);
      return;
    }
    setForm((prev) => ({ ...prev, category }));
  };

  const handlePublishedChange = (checked: boolean) => {
    setForm((prev) => ({ ...prev, isPublished: checked }));
  };

  const submitAnnouncement = async (payload: AnnouncementUpsertRequest) => {
    try {
      if (isNew) {
        await createAnnouncement.mutateAsync(payload);
        toast.success('공지사항을 등록했습니다.');
      } else {
        await updateAnnouncement.mutateAsync({
          id: announcementId,
          data: payload,
        });
        toast.success('공지사항을 수정했습니다.');
      }
      navigate({ to: ROUTE_PATHS.BOARD_ANNOUNCEMENTS } as never);
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const buildSavePayload = (
    sendAlimtalk: boolean,
  ): AnnouncementUpsertRequest | null => {
    if (!form.title.trim()) {
      toast.error('제목을 입력해 주세요.');
      return null;
    }

    if (!form.thumbnailUrl) {
      toast.error('썸네일 이미지를 선택해 주세요.');
      return null;
    }

    const startParts = splitDatetimeLocal(form.publishStartAt);
    const endParts = splitDatetimeLocal(form.publishEndAt);
    if (!startParts.date || !endParts.date) {
      toast.error('게시 기간을 선택해 주세요.');
      return null;
    }

    if (isScheduleNotice) {
      if (!scheduleForm.registrationYearMonth) {
        toast.error('등록년월을 선택해 주세요.');
        return null;
      }

      const scheduleValidation = validateScheduleRegistrationItems(
        scheduleForm.registrationScheduleItems,
      );
      if (!scheduleValidation.valid) {
        toast.error(
          scheduleValidation.message ?? '스케줄 정보를 확인해 주세요.',
        );
        return null;
      }

      // 일정 안내는 게시 고정. 알림톡은 버튼 선택으로만 true 를 보낸다.
      return {
        title: form.title.trim(),
        category: form.category,
        content: null,
        isPublished: true,
        isMainPublished: true,
        isAlimtalkSend: resolveNoticeAlimtalkSendPayload({
          category: form.category,
          isPublished: true,
          isAlimtalkSendChecked: sendAlimtalk,
        }),
        publishStartAt: getScheduleNoticePublishStartIso(),
        publishEndAt: toAnnouncementPublishIso(form.publishEndAt),
        thumbnailUrl: form.thumbnailUrl || undefined,
        schedule: buildAnnouncementSchedulePayload(scheduleForm),
      };
    }

    const cleanedContent = removeAnnouncementTableHeightStyles(form.content);
    if (isAnnouncementContentEmpty(cleanedContent)) {
      toast.error('본문 내용을 입력해 주세요.');
      return null;
    }

    return {
      title: form.title.trim(),
      category: form.category,
      content: cleanedContent,
      isPublished: form.isPublished,
      isMainPublished,
      // 일정 안내가 아니면 발송하지 않음 — 생략 시 서버가 true 로 읽으므로 false 고정
      isAlimtalkSend: false,
      publishStartAt: toAnnouncementPublishIso(form.publishStartAt),
      publishEndAt: toAnnouncementPublishIso(form.publishEndAt),
      thumbnailUrl: form.thumbnailUrl || undefined,
    };
  };

  const handleSave = (sendAlimtalk: boolean) => {
    const payload = buildSavePayload(sendAlimtalk);
    if (!payload) return;

    if (
      willSendNoticeAlimtalk({
        category: payload.category,
        isPublished: payload.isPublished,
        isAlimtalkSend: payload.isAlimtalkSend,
      })
    ) {
      setPendingPayload(payload);
      setAlimtalkConfirmOpen(true);
      return;
    }

    void submitAnnouncement(payload);
  };

  const handleThumbnailFileSelect = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (cropImageSrc) URL.revokeObjectURL(cropImageSrc);
    const objectUrl = URL.createObjectURL(file);
    setCropImageSrc(objectUrl);
    setCropFileName(file.name || 'thumbnail.jpg');
    setCropOpen(true);
  };

  const handleCroppedThumbnail = async (file: File) => {
    try {
      const imageUrl = await uploadImage.mutateAsync(file);
      setForm((prev) => ({ ...prev, thumbnailUrl: imageUrl }));
      setThumbnailPreview(imageUrl);
      toast.success('썸네일을 업로드했습니다.');
    } catch (err) {
      handleApiError(err, toast);
      throw err;
    }
  };

  const handleCropOpenChange = (open: boolean) => {
    setCropOpen(open);
    if (!open && cropImageSrc) {
      URL.revokeObjectURL(cropImageSrc);
      setCropImageSrc(null);
    }
  };

  const handleCancel = () => {
    if (isViewMode) {
      navigate({ to: ROUTE_PATHS.BOARD_ANNOUNCEMENTS } as never);
      return;
    }
    if (window.confirm('저장하지 않고 나가시겠습니까?')) {
      navigate({ to: ROUTE_PATHS.BOARD_ANNOUNCEMENTS } as never);
    }
  };

  const goList = () => {
    navigate({ to: ROUTE_PATHS.BOARD_ANNOUNCEMENTS } as never);
  };

  const categoryOptions = ANNOUNCEMENT_FORM_CATEGORIES.some(
    (c) => c.value === form.category,
  )
    ? ANNOUNCEMENT_FORM_CATEGORIES
    : ANNOUNCEMENT_CATEGORIES.filter(
        (c) =>
          ANNOUNCEMENT_FORM_CATEGORIES.some((f) => f.value === c.value) ||
          c.value === form.category,
      );

  const publicationStatus = computeAnnouncementPublicationStatus({
    isPublished: form.isPublished,
    publishedStartAt: form.publishStartAt,
    publishedEndAt: form.publishEndAt,
  });

  const isSaving = createAnnouncement.isPending || updateAnnouncement.isPending;

  if (!isNew && isLoading) {
    return <LoadingSpinner tip="공지사항을 불러오는 중..." />;
  }

  const pageTitle = isViewMode
    ? '공지사항 보기'
    : isNew
      ? '공지사항 등록'
      : '공지사항 수정';

  const publishStartParts = splitDatetimeLocal(form.publishStartAt);
  const publishEndParts = splitDatetimeLocal(form.publishEndAt);

  const updatePublishStartDate = (date: string) => {
    setForm((prev) => {
      const { time } = splitDatetimeLocal(prev.publishStartAt);
      return {
        ...prev,
        publishStartAt: date ? composeDatetimeLocal(date, time || '00:00') : '',
      };
    });
  };

  const updatePublishEndDate = (date: string) => {
    setForm((prev) => {
      const { time } = splitDatetimeLocal(prev.publishEndAt);
      return {
        ...prev,
        publishEndAt: date ? composeDatetimeLocal(date, time || '00:00') : '',
      };
    });
  };

  const updatePublishStartTime = (time: string) => {
    setForm((prev) => {
      const { date } = splitDatetimeLocal(prev.publishStartAt);
      if (!date) return prev;
      return { ...prev, publishStartAt: composeDatetimeLocal(date, time) };
    });
  };

  const updatePublishEndTime = (time: string) => {
    setForm((prev) => {
      const { date } = splitDatetimeLocal(prev.publishEndAt);
      if (!date) return prev;
      return { ...prev, publishEndAt: composeDatetimeLocal(date, time) };
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Megaphone}
        title={pageTitle}
        description={
          isViewMode
            ? '등록된 공지사항을 확인합니다'
            : isNew
              ? '새 공지사항을 등록합니다'
              : '공지사항 내용을 수정합니다'
        }
        actions={
          <FooterRow gap={8}>
            {isViewMode ? (
              <MenuWriteGate>
                <Button type="button" variant="outline" onClick={openEdit}>
                  <Pencil className="mr-2 h-4 w-4" />
                  수정
                </Button>
              </MenuWriteGate>
            ) : null}
            <Button type="button" variant="outline" onClick={goList}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              나가기
            </Button>
          </FooterRow>
        }
      />

      <Card>
        <CardContent className="space-y-6">
          {/* Row1: 카테고리 | 제목 */}
          <div className="grid gap-4 md:grid-cols-12">
            <Field label="카테고리" className="md:col-span-3 lg:col-span-2">
              <Select
                value={form.category}
                disabled={isReadOnly}
                onValueChange={handleCategoryChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="카테고리를 선택하세요" />
                </SelectTrigger>
                <SelectContent>
                  {categoryOptions.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="제목" className="md:col-span-9 lg:col-span-10">
              <Input
                value={form.title}
                readOnly={isReadOnly}
                placeholder="공지사항 제목을 입력하세요"
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, title: e.target.value }))
                }
              />
            </Field>
          </div>

          {/* Row2: 게시 상태 | 메인 노출 | 게시 기간 */}
          <div className="grid gap-4 md:grid-cols-12 md:items-end">
            <Field label="게시 상태" className="md:col-span-2">
              {isViewMode ? (
                <div className="flex h-10 flex-wrap items-center gap-2">
                  <Badge
                    variant={getAnnouncementPublicationStatusBadgeVariant(
                      publicationStatus,
                    )}
                  >
                    {getAnnouncementPublicationStatusLabel(publicationStatus)}
                  </Badge>
                  {/* 상세 보기 + 일정 안내만: 마지막 저장의 알림톡 발송 여부 */}
                  {isScheduleNotice && lastAlimtalkSendRequested != null ? (
                    <Badge
                      variant={getNoticeAlimtalkSendBadgeVariant(
                        lastAlimtalkSendRequested,
                      )}
                    >
                      {getNoticeAlimtalkSendLabel(lastAlimtalkSendRequested)}
                    </Badge>
                  ) : null}
                </div>
              ) : (
                <div className="flex h-10 items-center gap-2">
                  <Switch
                    checked={form.isPublished}
                    disabled={isScheduleNotice || isReadOnly}
                    onCheckedChange={handlePublishedChange}
                  />
                  <span className="text-sm tracking-tight text-ruda-text-secondary">
                    {form.isPublished ? '게시' : '미공개'}
                  </span>
                </div>
              )}
            </Field>
            <Field label="메인 페이지 노출" className="md:col-span-2">
              <div className="flex h-10 items-center gap-2">
                <Switch
                  checked={isMainPublished}
                  disabled={isScheduleNotice || isReadOnly}
                  onCheckedChange={setIsMainPublished}
                />
                <span className="text-sm tracking-tight text-ruda-text-secondary">
                  {isMainPublished ? '노출' : '미노출'}
                </span>
              </div>
            </Field>
            <Field label="게시 기간" className="md:col-span-8">
              <FooterRow gap={8} justify="start" wrap>
                <DatePicker
                  value={publishStartParts.date}
                  onChange={updatePublishStartDate}
                  disabled={isScheduleNotice || isReadOnly}
                  placeholder="시작일"
                  className="w-[150px]"
                />
                <TimePicker
                  value={
                    isScheduleNotice
                      ? '00:00'
                      : publishStartParts.time || '00:00'
                  }
                  onChange={updatePublishStartTime}
                  disabled={isScheduleNotice || isReadOnly}
                  minuteStep={10}
                  placeholder="시작 시간"
                  className="w-[120px]"
                />
                <span className="text-sm text-ruda-text-tertiary">~</span>
                <DatePicker
                  value={publishEndParts.date}
                  onChange={updatePublishEndDate}
                  disabled={isReadOnly}
                  placeholder="종료일"
                  className="w-[150px]"
                />
                <TimePicker
                  value={publishEndParts.time || '00:00'}
                  onChange={updatePublishEndTime}
                  disabled={isReadOnly}
                  minuteStep={10}
                  placeholder="종료 시간"
                  className="w-[120px]"
                />
              </FooterRow>
            </Field>
          </div>

          {/* 썸네일 — 레거시 ImgCrop 1:1 + 업로드 압축 */}
          <Field
            label="썸네일 이미지"
            hint="썸네일 이미지는 1:1비율로 1장만 선택할 수 있습니다."
          >
            <div className="flex flex-wrap items-start gap-4">
              <div className="flex h-[200px] w-[200px] items-center justify-center overflow-hidden rounded-12 border border-ruda-border-light bg-ruda-grey-50">
                {thumbnailPreview ? (
                  <img
                    src={thumbnailPreview}
                    alt="썸네일"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <p className="px-4 text-center text-sm tracking-tight text-ruda-text-tertiary">
                    등록된 썸네일이 없습니다
                  </p>
                )}
              </div>
              {!isReadOnly ? (
                <FileUploadButton
                  className="ruda-button-outline"
                  accept="image/png,image/jpeg,image/jpg"
                  onChange={handleThumbnailFileSelect}
                >
                  업로드
                </FileUploadButton>
              ) : null}
            </div>
          </Field>

          <ImageCropDialog
            open={cropOpen}
            imageSrc={cropImageSrc}
            fileName={cropFileName}
            aspect={1}
            onOpenChange={handleCropOpenChange}
            onConfirm={handleCroppedThumbnail}
          />

          {/* 일정 안내 OR 본문 */}
          {isScheduleNotice ? (
            <AnnouncementScheduleSection
              value={scheduleForm}
              onChange={setScheduleForm}
              disabled={isReadOnly}
            />
          ) : (
            <Field label="내용">
              <TinyMceEditor
                value={form.content}
                readOnly={isReadOnly}
                onChange={(html) =>
                  setForm((prev) => ({ ...prev, content: html }))
                }
              />
            </Field>
          )}

          {!isReadOnly ? (
            <>
              <Separator className="bg-ruda-border-light" />
              <FooterRow gap={8} wrap>
                <Button
                  type="button"
                  variant="outline"
                  className="ruda-button-outline"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  취소
                </Button>
                <Button
                  type="button"
                  className="ruda-button"
                  onClick={() => handleSave(false)}
                  disabled={isSaving}
                >
                  <Save className="mr-2 h-4 w-4" />
                  {isNew ? '등록' : '수정'}
                </Button>
                {showAlimtalkSendButton ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="ruda-button-outline"
                    onClick={() => handleSave(true)}
                    disabled={isSaving || !canSendAlimtalk}
                  >
                    {isNew ? '등록 + 알림톡 전송' : '수정 + 알림톡 전송'}
                  </Button>
                ) : null}
              </FooterRow>
            </>
          ) : null}
        </CardContent>
      </Card>

      <AlertDialog
        open={alimtalkConfirmOpen}
        onOpenChange={(open) => {
          setAlimtalkConfirmOpen(open);
          if (!open) setPendingPayload(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>알림톡 발송 확인</AlertDialogTitle>
            <AlertDialogDescription>
              저장 시 승인 거래처 전체에 일정 안내 알림톡이 발송됩니다.
              계속할까요?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSaving}>취소</AlertDialogCancel>
            <AlertDialogAction
              className="ruda-button"
              disabled={isSaving || !pendingPayload}
              onClick={(e) => {
                e.preventDefault();
                if (!pendingPayload) return;
                const payload = pendingPayload;
                setAlimtalkConfirmOpen(false);
                setPendingPayload(null);
                void submitAnnouncement(payload);
              }}
            >
              {isNew ? '등록 및 발송' : '수정 및 발송'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
